import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { CATEGORIES, modulesIn, moduleHref } from "@/lib/modules";
import { Container, Section, SectionHeader } from "@/components/ui-kit";
import { BrowserFrame, PhoneFrame } from "@/components/mockups/primitives";
import { Mockup, PHONE_MOCKUPS } from "@/components/mockups";
import { cn } from "@/lib/utils";

const short = (s) => { const first = s.split(". ")[0]; const t = first.length > 110 ? first.slice(0, 108).replace(/[,;:\s]+\S*$/, "") + "…" : first; return /[.…]$/.test(t) ? t : t + "."; };

export const ModuleCard = ({ m, size = "sm", testIdPrefix = "module-card" }) => (
  <Link to={moduleHref(m)} data-testid={`${testIdPrefix}-${m.slug}`} className={cn("card-dark group flex flex-col p-5", size === "lg" && "p-6 sm:p-8")}>
    <div className="flex items-start justify-between gap-3">
      <span className="grid place-items-center h-10 w-10 rounded-xl border border-mk-border bg-mk-bg text-mk-lime group-hover:border-mk-lime/50 group-hover:shadow-[0_0_20px_rgba(214,239,55,0.25)] transition-all"><m.icon className="h-5 w-5" /></span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-mk-muted text-right">{m.isNew && <span className="mr-2 rounded-full border border-mk-lime/40 bg-mk-lime/10 px-1.5 py-0.5 text-mk-lime" data-testid={`${testIdPrefix}-${m.slug}-new`}>New</span>}{m.kicker}</span>
    </div>
    <h3 className={cn("mt-4 font-display font-semibold text-mk-text", size === "lg" ? "text-xl sm:text-2xl" : "text-base")}>{m.name}</h3>
    <p className={cn("mt-2 text-mk-dim leading-relaxed", size === "lg" ? "text-sm sm:text-base" : "text-[13px]")}>{size === "lg" ? m.summary : short(m.summary)}</p>
    <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-medium text-mk-text group-hover:text-mk-lime transition-colors">Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
  </Link>
);

const ModuleExplorer = () => {
  const [active, setActive] = useState(CATEGORIES[0].slug);
  const cat = CATEGORIES.find((c) => c.slug === active);
  const mods = modulesIn(active);
  const featured = mods.find((m) => m.flagship) || mods[0];
  const rest = mods.filter((m) => m.slug !== featured.slug);

  return (
    <Section id="platform" className="bg-mk-surface/40 border-y border-mk-border" data-testid="module-explorer">
      <Container>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeader eyebrow="Every inch of the platform" title="One engine for every moving part of maintenance ops." desc="Jobs, quotes, invoices, operatives, suppliers, compliance, assets, AI, webhooks — if it touches maintenance, Fixlr has a dedicated, production-grade module for it." />
          <Link to="/product" data-testid="module-explorer-all" className="btn-ghost shrink-0 self-start lg:self-auto">All {CATEGORIES.reduce((n, c) => n + modulesIn(c.slug).length, 0)} modules <ArrowUpRight className="h-4 w-4" /></Link>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 p-1.5 rounded-2xl border border-mk-border bg-mk-bg w-fit" role="tablist">
          {CATEGORIES.map((c) => (
            <button key={c.slug} type="button" role="tab" aria-selected={active === c.slug} onClick={() => setActive(c.slug)} data-testid={`module-tab-${c.slug}`}
              className={cn("relative rounded-xl px-4 py-2 text-sm font-medium transition-colors", active === c.slug ? "text-mk-bg" : "text-mk-dim hover:text-mk-text")}>
              {active === c.slug && <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-xl bg-mk-lime" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
              <span className="relative z-10 flex items-center gap-2">{c.name}<span className={cn("font-mono text-[10px] rounded-full px-1.5 py-0.5", active === c.slug ? "bg-mk-bg/15" : "bg-mk-raised")}>{modulesIn(c.slug).length}</span></span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }} className="mt-8">
            <div className="grid lg:grid-cols-12 gap-6">
              <Link to={moduleHref(featured)} data-testid={`module-featured-${featured.slug}`} className="lg:col-span-7 min-w-0 card-dark group p-6 sm:p-8 flex flex-col">
                <div className="flex items-center justify-between gap-3">
                  <span className="eyebrow">{cat.kicker}</span>
                  <span className="font-mono text-[10px] text-mk-muted">Featured · {featured.kicker}</span>
                </div>
                <h3 className="mt-4 font-display text-2xl sm:text-3xl font-bold text-mk-text text-balance">{featured.headline}</h3>
                <p className="mt-3 text-mk-dim leading-relaxed max-w-xl">{featured.summary}</p>
                <div className="mt-6 relative">
                  {PHONE_MOCKUPS.has(featured.mockup) ? (
                    <div className="py-4"><Mockup name={featured.mockup} /></div>
                  ) : (
                    <BrowserFrame url={featured.liveUrl?.replace("https://", "")} className="pointer-events-none">
                      <Mockup name={featured.mockup} compact />
                    </BrowserFrame>
                  )}
                </div>
                <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-mk-text group-hover:text-mk-lime transition-colors">Explore {featured.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
              </Link>
              <div className="lg:col-span-5 min-w-0 grid sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4 content-start">
                {rest.map((m) => <ModuleCard key={m.slug} m={m} />)}
                <Link to={`/platform/${cat.slug}`} data-testid={`module-explorer-cat-${cat.slug}`} className="rounded-2xl border border-dashed border-mk-strong p-5 flex flex-col justify-between hover:border-mk-lime/60 transition-colors group">
                  <span className="text-sm text-mk-dim">{cat.desc}</span>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-mk-lime">See all {cat.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </Container>
    </Section>
  );
};

export default ModuleExplorer;
export { PhoneFrame };
